import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Upload, Image as ImgIcon, Video, Trash2, Sparkles, Instagram, Clock, Save, Download, Facebook } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/Page";

const BrandStyle = () => {
  const { brandId } = useParams();
  const [brand, setBrand] = useState(null);
  const [settings, setSettings] = useState({});
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [connectingIg, setConnectingIg] = useState(false);
  const [showManualIg, setShowManualIg] = useState(false);
  const [manualIgId, setManualIgId] = useState("");
  const [savingManualIg, setSavingManualIg] = useState(false);
  const [scrapingFb, setScrapingFb] = useState(false);
  const [scrapeLimit, setScrapeLimit] = useState(10);
  const [uploadType, setUploadType] = useState("photo");
  const [uploadCaption, setUploadCaption] = useState("");
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showHeyGenBrowser, setShowHeyGenBrowser] = useState(false);
  const [hgAvatars, setHgAvatars] = useState([]);
  const [hgVoices, setHgVoices] = useState([]);
  const [hgLoading, setHgLoading] = useState(false);
  const [hgTab, setHgTab] = useState("avatars");
  const [kbUrl, setKbUrl] = useState("");
  const [scrapingKb, setScrapingKb] = useState(false);
  const [newTheme, setNewTheme] = useState("");
  const [programs, setPrograms] = useState([]);
  const [newProgName, setNewProgName] = useState("");
  const [newProgUrl, setNewProgUrl] = useState("");
  const [bulkText, setBulkText] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [progSaving, setProgSaving] = useState({}); // pid -> bool
  const [scrapingAll, setScrapingAll] = useState(false);

  useEffect(() => {
    if (!showHeyGenBrowser) return;
    if (hgAvatars.length > 0 && hgVoices.length > 0) return;
    setHgLoading(true);
    Promise.all([
      api.get("/heygen/avatars"),
      api.get("/heygen/voices", { params: { language: "English" } }),
    ]).then(([a, v]) => {
      setHgAvatars(a.data.avatars || []);
      setHgVoices(v.data.voices || []);
    }).catch((e) => toast.error(e?.response?.data?.detail || "HeyGen load failed"))
      .finally(() => setHgLoading(false));
  }, [showHeyGenBrowser, hgAvatars.length, hgVoices.length]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, s, p] = await Promise.all([
        api.get(`/brands/${brandId}`),
        api.get(`/brands/${brandId}/style`),
        api.get(`/brands/${brandId}/programs`).catch(() => ({ data: { programs: [] } })),
      ]);
      setBrand(b.data);
      setSettings(s.data.settings || {});
      setSamples(s.data.samples || []);
      setPrograms(p.data.programs || []);
    } catch (e) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [brandId]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.patch(`/brands/${brandId}/style`, {
        post_hours_start: settings.post_hours_start ?? 8,
        post_hours_end: settings.post_hours_end ?? 17,
        default_avatar_id: settings.default_avatar_id || null,
        default_voice_id: settings.default_voice_id || null,
        custom_prompt: settings.custom_prompt || null,
        knowledge_notes: settings.knowledge_notes || null,
        content_themes: settings.content_themes || [],
      });
      toast.success("Settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const connectIg = async () => {
    setConnectingIg(true);
    try {
      const { data } = await api.post(`/brands/${brandId}/connect-instagram`);
      toast.success(`Instagram Business Account linked: ${data.ig_business_id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to link Instagram");
    } finally {
      setConnectingIg(false);
    }
  };

  const saveManualIg = async () => {
    const id = manualIgId.trim();
    if (!/^\d{5,}$/.test(id)) {
      toast.error("IG Business ID must be a numeric ID (find it in Meta Business Suite → IG Account → About)");
      return;
    }
    setSavingManualIg(true);
    try {
      await api.patch(`/brands/${brandId}/style`, { ig_business_id: id });
      toast.success("Instagram Business ID saved");
      setShowManualIg(false);
      setManualIgId("");
      load();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      toast.error(`Failed to save (${status || "no response"}): ${detail}`);
      console.error("Manual IG save error:", e?.response || e);
    } finally {
      setSavingManualIg(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFile(true);
    try {
      // Read file as base64 data URL
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post(`/brands/${brandId}/style/samples`, {
        type: uploadType,
        media_url: dataUrl,
        caption: uploadCaption || null,
      });
      toast.success("Sample added");
      setUploadCaption("");
      e.target.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingFile(false);
    }
  };

  const deleteSample = async (s) => {
    if (!confirm("Delete this sample?")) return;
    try {
      await api.delete(`/brands/${brandId}/style/samples/${s.id}`);
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const scrapeFb = async () => {
    if (!brand?.fb_page_id) {
      toast.error("Connect Facebook Page first");
      return;
    }
    setScrapingFb(true);
    try {
      const { data } = await api.post(`/brands/${brandId}/style/scrape-fb`, { limit: scrapeLimit });
      const parts = [];
      if (data.imported) parts.push(`${data.imported} imported`);
      if (data.skipped_duplicates) parts.push(`${data.skipped_duplicates} dup skipped`);
      if (data.skipped_no_media) parts.push(`${data.skipped_no_media} text-only skipped`);
      toast.success(`From FB: ${parts.join(" · ") || "nothing new"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "FB scrape failed");
    } finally {
      setScrapingFb(false);
    }
  };

  const scrapeKbUrl = async () => {
    const url = kbUrl.trim();
    if (!/^https?:\/\//.test(url)) {
      toast.error("URL must start with http:// or https://");
      return;
    }
    setScrapingKb(true);
    try {
      await api.post(`/brands/${brandId}/knowledge/scrape`, { url });
      toast.success("Page scraped and added to knowledge base");
      setKbUrl("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scrape failed");
    } finally {
      setScrapingKb(false);
    }
  };

  const deleteKbSource = async (url) => {
    if (!confirm(`Remove this source?\n${url}`)) return;
    try {
      await api.delete(`/brands/${brandId}/knowledge/source`, { params: { url } });
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const addTheme = () => {
    const t = newTheme.trim();
    if (!t) return;
    const list = settings.content_themes || [];
    if (list.includes(t)) {
      toast.error("Theme already added");
      return;
    }
    setSettings({ ...settings, content_themes: [...list, t] });
    setNewTheme("");
  };

  const removeTheme = (theme) => {
    setSettings({ ...settings, content_themes: (settings.content_themes || []).filter((t) => t !== theme) });
  };

  // ---- Programs ----
  const addProgram = async () => {
    const name = newProgName.trim();
    if (!name) { toast.error("Program name required"); return; }
    try {
      await api.post(`/brands/${brandId}/programs`, {
        name, detail_url: newProgUrl.trim() || null,
      });
      setNewProgName(""); setNewProgUrl("");
      toast.success(`Added "${name}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  };

  const bulkAddPrograms = async () => {
    const names = bulkText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (!names.length) { toast.error("Paste at least one name"); return; }
    try {
      const { data } = await api.post(`/brands/${brandId}/programs/bulk`, { names });
      toast.success(`Added ${data.added} program${data.added === 1 ? "" : "s"}`);
      setBulkText(""); setShowBulk(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk add failed");
    }
  };

  const updateProgramField = async (pid, patch) => {
    setProgSaving((p) => ({ ...p, [pid]: true }));
    try {
      const { data } = await api.patch(`/brands/${brandId}/programs/${pid}`, patch);
      setPrograms((prev) => prev.map((p) => (p.id === pid ? data : p)));
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setProgSaving((p) => ({ ...p, [pid]: false }));
    }
  };

  const scrapeProgram = async (pid) => {
    setProgSaving((p) => ({ ...p, [pid]: true }));
    try {
      const { data } = await api.post(`/brands/${brandId}/programs/${pid}/scrape`);
      setPrograms((prev) => prev.map((p) => (p.id === pid ? data : p)));
      toast.success("Program details scraped");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scrape failed");
    } finally {
      setProgSaving((p) => ({ ...p, [pid]: false }));
    }
  };

  const deleteProgram = async (pid, name) => {
    if (!confirm(`Delete program "${name}"?`)) return;
    try {
      await api.delete(`/brands/${brandId}/programs/${pid}`);
      setPrograms((prev) => prev.filter((p) => p.id !== pid));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const scrapeAllPrograms = async () => {
    const withUrl = programs.filter((p) => (p.detail_url || "").trim() && p.active !== false);
    if (!withUrl.length) {
      toast.error("No programs have a detail URL yet — add URLs first");
      return;
    }
    if (!confirm(`Scrape ${withUrl.length} program${withUrl.length === 1 ? "" : "s"}? This may take 1-2 minutes.`)) return;
    setScrapingAll(true);
    try {
      const { data } = await api.post(`/brands/${brandId}/programs/scrape-all`, {}, { timeout: 240000 });
      const parts = [];
      if (data.ok) parts.push(`${data.ok} ok`);
      if (data.failed) parts.push(`${data.failed} failed`);
      if (data.skipped) parts.push(`${data.skipped} skipped`);
      toast.success(`Scrape complete: ${parts.join(" · ")}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scrape-all failed");
    } finally {
      setScrapingAll(false);
    }
  };

  const analyze = async () => {
    if (samples.length < 3) {
      if (!confirm("Less than 3 samples — analysis may be weak. Continue anyway?")) return;
    }
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/brands/${brandId}/style/analyze`);
      toast.success("Style fingerprint generated");
      setSettings((p) => ({ ...p, style_fingerprint: data.fingerprint }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analyze failed");
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) return <PageContainer><div className="op-card p-10 text-center text-gray-500">Loading…</div></PageContainer>;
  if (!brand) return <PageContainer><div className="op-card p-10 text-center text-gray-500">Brand not found</div></PageContainer>;

  const fp = settings.style_fingerprint;

  return (
    <PageContainer>
      <Link to="/brands" className="text-sm text-gray-500 hover:text-[#7C3AED] inline-flex items-center gap-1 mb-2"><ArrowLeft size={14} /> Back to Brands</Link>
      <PageHeader title={`${brand.name} — Style Library`}
                  subtitle="Upload sample posts (photo + video) so AI learns your voice. Then connect Instagram + configure posting hours."
                  testId="brand-style-header" />

      {/* Connections */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <div className="op-card p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Facebook Page</p>
          {brand.fb_page_id ? <>
            <p className="text-sm font-medium text-emerald-600">✓ Connected</p>
            <p className="text-xs text-gray-500 mt-1">{brand.fb_page_name || brand.fb_page_id}</p>
          </> : <p className="text-sm text-gray-400">Not connected — set up in Brands page</p>}
        </div>
        <div className="op-card p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Instagram Business</p>
          {settings.ig_business_id ? <>
            <p className="text-sm font-medium text-emerald-600">✓ Linked</p>
            <p className="text-xs text-gray-500 mt-1 break-all">{settings.ig_business_id}</p>
            <button onClick={() => { setManualIgId(settings.ig_business_id); setShowManualIg(true); }}
                    className="text-xs text-[#7C3AED] hover:underline mt-2" data-testid="edit-ig-id-btn">
              Change ID
            </button>
          </> : <>
            <button onClick={connectIg} disabled={connectingIg || !brand.fb_page_id} className="op-btn-primary text-xs w-full mb-2" data-testid="connect-ig-btn">
              <Instagram size={14} /> {connectingIg ? "Detecting…" : "Auto-link from FB Page"}
            </button>
            <button onClick={() => setShowManualIg(true)} className="text-xs text-[#7C3AED] hover:underline w-full text-left" data-testid="manual-ig-btn">
              Enter IG Business ID manually →
            </button>
          </>}
        </div>
        <div className="op-card p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-1">HeyGen Avatar (for stories)</p>
          <input className="op-input text-xs mb-1" placeholder="Avatar ID" value={settings.default_avatar_id || ""}
                 onChange={(e) => setSettings({ ...settings, default_avatar_id: e.target.value })} />
          <input className="op-input text-xs" placeholder="Voice ID" value={settings.default_voice_id || ""}
                 onChange={(e) => setSettings({ ...settings, default_voice_id: e.target.value })} />
          <button type="button" onClick={() => setShowHeyGenBrowser(true)} className="text-xs text-[#7C3AED] hover:underline mt-2" data-testid="heygen-browse-btn">
            Browse avatars + voices →
          </button>
        </div>
      </div>

      {/* Posting hours + extra prompt */}
      <div className="op-card p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Clock size={16} /> Posting Schedule</h3>
          <button onClick={saveSettings} disabled={savingSettings} className="op-btn-primary text-sm" data-testid="save-style-settings">
            <Save size={14} /> {savingSettings ? "Saving…" : "Save Settings"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Start hour (UTC, 0-23)</label>
            <input type="number" min="0" max="23" className="op-input" value={settings.post_hours_start ?? 8} onChange={(e) => setSettings({ ...settings, post_hours_start: Number(e.target.value) })} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">End hour (UTC, 0-23)</label>
            <input type="number" min="0" max="23" className="op-input" value={settings.post_hours_end ?? 17} onChange={(e) => setSettings({ ...settings, post_hours_end: Number(e.target.value) })} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Extra creative direction (optional)</label>
            <input className="op-input" placeholder="e.g., Always include emojis" value={settings.custom_prompt || ""} onChange={(e) => setSettings({ ...settings, custom_prompt: e.target.value })} />
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2">Default: 8am-5pm UTC = 10 hourly slots. AI will spread approved drafts across these hours.</p>
      </div>

      {/* Knowledge Base */}
      <div className="op-card p-4 mb-5">
        <h3 className="font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Sparkles size={16} className="text-[#7C3AED]" /> Knowledge Base
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Feed the AI real facts about your business so captions stop being generic. Add your website (programs, pricing, locations) + free-text notes.
        </p>

        {/* URL scrape */}
        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-1">Website URL to scrape (programs page, about, services, etc.)</label>
          <div className="flex gap-2 flex-wrap">
            <input
              className="op-input flex-1 min-w-[260px]"
              placeholder="https://yourcollege.com/programs"
              value={kbUrl}
              onChange={(e) => setKbUrl(e.target.value)}
              data-testid="kb-url-input"
            />
            <button onClick={scrapeKbUrl} disabled={scrapingKb || !kbUrl.trim()} className="op-btn-primary text-sm" data-testid="kb-scrape-btn">
              <Download size={14} /> {scrapingKb ? "Scraping…" : "Scrape & Add"}
            </button>
          </div>
        </div>

        {/* Existing sources */}
        {(settings.knowledge_urls || []).length > 0 && (
          <div className="space-y-2 mb-4" data-testid="kb-sources-list">
            {(settings.knowledge_urls || []).map((src) => (
              <div key={src.url} className="border border-gray-200 rounded-lg p-3 text-xs">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <a href={src.url} target="_blank" rel="noopener noreferrer" className="text-[#7C3AED] font-medium truncate flex-1 hover:underline">
                    {src.title || src.url}
                  </a>
                  <button onClick={() => deleteKbSource(src.url)} className="text-gray-400 hover:text-rose-600 shrink-0">
                    <Trash2 size={12} />
                  </button>
                </div>
                {src.summary && <p className="text-gray-600 mb-1">{src.summary}</p>}
                {(src.key_facts || []).length > 0 && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-gray-500 hover:text-gray-700">View {src.key_facts.length} key facts</summary>
                    <ul className="mt-2 ml-4 list-disc text-gray-600 space-y-0.5">
                      {src.key_facts.slice(0, 25).map((f, i) => <li key={i}>{f}</li>)}
                    </ul>
                  </details>
                )}
                <p className="text-[10px] text-gray-400 mt-1">Last scraped: {src.last_scraped_at?.slice(0, 16).replace("T", " ")}</p>
              </div>
            ))}
          </div>
        )}

        {/* Owner notes */}
        <div className="mb-4">
          <label className="block text-xs text-gray-500 mb-1">Owner notes (free text — programs, USPs, target audience, anything the website doesn't say)</label>
          <textarea
            className="op-input w-full min-h-[100px] text-sm"
            placeholder="e.g., We offer a 2-year Diploma in Business Management, 1-year Hospitality program, located in Surrey BC. Target audience: international students from India and Philippines. PGWP eligible programs. Job placement rate 92%."
            value={settings.knowledge_notes || ""}
            onChange={(e) => setSettings({ ...settings, knowledge_notes: e.target.value })}
            data-testid="kb-notes-textarea"
          />
        </div>

        {/* Content Themes */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">Content Themes (AI will rotate through these instead of generic stuff)</label>
          <div className="flex gap-2 flex-wrap mb-2">
            <input
              className="op-input flex-1 min-w-[200px] text-sm"
              placeholder="e.g., Program Highlights, Career Outcomes, Tuition & Funding, Student Life in BC"
              value={newTheme}
              onChange={(e) => setNewTheme(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTheme(); } }}
              data-testid="theme-input"
            />
            <button onClick={addTheme} disabled={!newTheme.trim()} className="op-btn-secondary text-sm" data-testid="add-theme-btn">+ Add</button>
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="themes-list">
            {(settings.content_themes || []).length === 0 && (
              <p className="text-xs text-gray-400 italic">No themes set — AI will default to: program highlights, career outcomes, student stories, FAQs.</p>
            )}
            {(settings.content_themes || []).map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-50 text-[#7C3AED] text-xs border border-purple-200">
                {t}
                <button onClick={() => removeTheme(t)} className="hover:text-rose-600" aria-label={`Remove ${t}`}>×</button>
              </span>
            ))}
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
          <button onClick={saveSettings} disabled={savingSettings} className="op-btn-primary text-sm" data-testid="kb-save-btn">
            <Save size={14} /> {savingSettings ? "Saving…" : "Save Knowledge & Themes"}
          </button>
        </div>
      </div>

      {/* Programs Library */}
      <div className="op-card p-4 mb-5">
        <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles size={16} className="text-[#7C3AED]" /> Programs Library
              <span className="text-xs text-gray-500 font-normal">({programs.length})</span>
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Each program drives content generation. AI will rotate through 6 content types per program (Info, Career Outcomes, Job Market, Student Aid, Reviews, BC Jobs).
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={scrapeAllPrograms}
              disabled={scrapingAll || programs.filter((p) => p.detail_url).length === 0}
              className="op-btn-secondary text-xs"
              data-testid="scrape-all-btn"
              title="Scrape every program that has a URL"
            >
              <Download size={12} /> {scrapingAll ? "Scraping all…" : "Scrape all"}
            </button>
            <button onClick={() => setShowBulk((v) => !v)} className="op-btn-secondary text-xs" data-testid="bulk-add-toggle">
              {showBulk ? "Cancel bulk" : "Bulk add"}
            </button>
          </div>
        </div>

        {/* Bulk add area */}
        {showBulk && (
          <div className="bg-purple-50/40 border border-purple-200 rounded-lg p-3 mb-3">
            <label className="block text-xs font-semibold text-gray-700 mb-1">Paste programs (one per line)</label>
            <textarea
              className="op-input w-full min-h-[140px] text-sm font-mono"
              placeholder={"Diploma in Accounting & Payroll\nDiploma in Digital Marketing Management\nDiploma in Human Resource Administration\n…"}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              data-testid="bulk-text"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => { setBulkText(""); setShowBulk(false); }} className="op-btn-secondary text-xs">Cancel</button>
              <button onClick={bulkAddPrograms} disabled={!bulkText.trim()} className="op-btn-primary text-xs" data-testid="bulk-add-submit">+ Add all</button>
            </div>
          </div>
        )}

        {/* Single add row */}
        <div className="flex gap-2 flex-wrap items-end mb-3 pb-3 border-b border-gray-100">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] text-gray-500 mb-1">Program name</label>
            <input className="op-input text-sm" placeholder="e.g., Diploma in Hospitality Management"
                   value={newProgName} onChange={(e) => setNewProgName(e.target.value)} data-testid="new-program-name" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] text-gray-500 mb-1">Detail URL (optional)</label>
            <input className="op-input text-sm" placeholder="https://hcc.ca/programs/hospitality"
                   value={newProgUrl} onChange={(e) => setNewProgUrl(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addProgram(); } }}
                   data-testid="new-program-url" />
          </div>
          <button onClick={addProgram} disabled={!newProgName.trim()} className="op-btn-primary text-sm" data-testid="add-program-btn">
            + Add program
          </button>
        </div>

        {/* Programs list */}
        {programs.length === 0 ? (
          <p className="text-sm text-gray-400 italic text-center py-4">
            No programs added yet. Use "Bulk add" to paste your list, or add one at a time above.
          </p>
        ) : (
          <div className="space-y-2" data-testid="programs-list">
            {programs.map((p) => {
              const sd = p.scraped_data || {};
              const isActive = p.active !== false;
              return (
                <div key={p.id} className={`border rounded-lg p-3 ${isActive ? "border-gray-200" : "border-gray-200 bg-gray-50 opacity-60"}`} data-testid={`program-${p.id}`}>
                  <div className="flex items-start gap-2 flex-wrap mb-2">
                    <input
                      className="op-input text-sm font-medium flex-1 min-w-[200px]"
                      defaultValue={p.name}
                      onBlur={(e) => e.target.value !== p.name && updateProgramField(p.id, { name: e.target.value })}
                    />
                    <label className="text-xs text-gray-600 flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={isActive} onChange={(e) => updateProgramField(p.id, { active: e.target.checked })} />
                      Active
                    </label>
                    <button onClick={() => deleteProgram(p.id, p.name)} className="text-gray-400 hover:text-rose-600" title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="flex gap-2 flex-wrap items-end">
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-[10px] text-gray-500 mb-1">Detail URL</label>
                      <input
                        className="op-input text-xs"
                        placeholder="https://…"
                        defaultValue={p.detail_url || ""}
                        onBlur={(e) => {
                          const v = e.target.value.trim() || null;
                          if (v !== (p.detail_url || null)) updateProgramField(p.id, { detail_url: v || "" });
                        }}
                      />
                    </div>
                    <button
                      onClick={() => scrapeProgram(p.id)}
                      disabled={!p.detail_url || progSaving[p.id]}
                      className="op-btn-secondary text-xs"
                      data-testid={`scrape-program-${p.id}`}
                    >
                      <Download size={12} /> {progSaving[p.id] ? "Scraping…" : (p.last_scraped_at ? "Re-scrape" : "Scrape")}
                    </button>
                  </div>
                  {p.scraped_summary && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-[#7C3AED] hover:underline">
                        ✓ Scraped — view extracted facts
                      </summary>
                      <div className="mt-2 bg-purple-50/30 border border-purple-100 rounded p-2 text-xs space-y-1">
                        <p><b>Summary:</b> {p.scraped_summary}</p>
                        {sd.duration && <p><b>Duration:</b> {sd.duration}</p>}
                        {sd.tuition && <p><b>Tuition:</b> {sd.tuition}</p>}
                        {sd.intake_months && (
                          <p><b>Intakes:</b> {Array.isArray(sd.intake_months) ? sd.intake_months.join(", ") : sd.intake_months}</p>
                        )}
                        {sd.key_curriculum?.length > 0 && (
                          <p><b>Curriculum:</b> {sd.key_curriculum.slice(0, 8).join(", ")}</p>
                        )}
                        {sd.career_paths?.length > 0 && (
                          <p><b>Career paths:</b> {sd.career_paths.slice(0, 8).join(", ")}</p>
                        )}
                        {sd.unique_selling_points?.length > 0 && (
                          <div><b>USPs:</b>
                            <ul className="ml-4 list-disc">{sd.unique_selling_points.slice(0, 5).map((u, i) => <li key={i}>{u}</li>)}</ul>
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Upload + samples */}
      <div className="op-card p-4 mb-5">
        <h3 className="font-semibold text-gray-900 mb-3">Style Samples ({samples.length})</h3>

        {/* Auto-import from Facebook Page */}
        <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Facebook size={14} className="text-[#1877F2]" /> Auto-import from Facebook Page
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                {brand.fb_page_id
                  ? `Pull recent posts from ${brand.fb_page_name || brand.fb_page_id} — captions + photos/videos.`
                  : "Connect a Facebook Page on the Brands page first."}
              </p>
            </div>
            <div>
              <label className="block text-[10px] text-gray-500 mb-1">Limit</label>
              <select className="op-input w-24 text-sm" value={scrapeLimit}
                      onChange={(e) => setScrapeLimit(Number(e.target.value))}
                      data-testid="fb-scrape-limit">
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
            <button onClick={scrapeFb}
                    disabled={scrapingFb || !brand.fb_page_id}
                    className="op-btn-primary text-sm self-end"
                    data-testid="fb-scrape-btn">
              <Download size={14} /> {scrapingFb ? "Importing…" : "Import from FB"}
            </button>
          </div>
        </div>

        <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider font-semibold">Or upload manually</p>
        <div className="flex flex-wrap items-end gap-3 mb-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <select className="op-input w-32" value={uploadType} onChange={(e) => setUploadType(e.target.value)}>
              <option value="photo">Photo</option>
              <option value="video">Video</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-500 mb-1">Original caption (paste your real caption)</label>
            <input className="op-input" placeholder="What you actually wrote when you posted this..." value={uploadCaption} onChange={(e) => setUploadCaption(e.target.value)} />
          </div>
          <label className={`op-btn-primary cursor-pointer ${uploadingFile ? "opacity-50" : ""}`} data-testid="upload-sample-label">
            <Upload size={14} /> {uploadingFile ? "Uploading…" : "Upload Sample"}
            <input type="file" accept={uploadType === "photo" ? "image/*" : "video/*"} onChange={handleFileUpload} className="hidden" disabled={uploadingFile} />
          </label>
        </div>
        {samples.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">No samples yet. Upload past posts so AI learns your voice.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {samples.map((s) => (
              <div key={s.id} className="border border-gray-200 rounded-lg overflow-hidden group relative" data-testid={`sample-${s.id}`}>
                <div className="aspect-square bg-gray-100">
                  {s.type === "video" ? <video src={s.media_url} className="w-full h-full object-cover" /> : <img src={s.media_url} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="p-2 text-xs text-gray-600 line-clamp-2 min-h-[2rem]">{s.caption || <span className="text-gray-400 italic">no caption</span>}</div>
                <button onClick={() => deleteSample(s)} className="absolute top-1 right-1 p-1 bg-white/90 rounded text-gray-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition">
                  <Trash2 size={12} />
                </button>
                <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-white/90 rounded text-[10px] text-gray-600">
                  {s.type === "video" ? <Video size={10} className="inline" /> : <ImgIcon size={10} className="inline" />}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Style fingerprint */}
      <div className="op-card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Sparkles size={16} className="text-[#7C3AED]" /> Style Fingerprint</h3>
          <button onClick={analyze} disabled={analyzing} className="op-btn-primary text-sm" data-testid="analyze-style-btn">
            <Sparkles size={14} /> {analyzing ? "Analyzing…" : fp ? "Re-analyze" : "Generate from samples"}
          </button>
        </div>
        {fp ? (
          <div className="space-y-2 text-sm">
            <div><span className="text-gray-500">Tone:</span> {(fp.tone_keywords || []).map((t, i) => <span key={i} className="ml-1 px-2 py-0.5 bg-purple-50 text-[#7C3AED] rounded text-xs">{t}</span>)}</div>
            <div><span className="text-gray-500">Caption style:</span> <code className="text-xs bg-gray-100 px-1 rounded">{fp.caption_style}</code></div>
            <div><span className="text-gray-500">Hashtag density:</span> <code className="text-xs bg-gray-100 px-1 rounded">{fp.hashtag_density}</code></div>
            {fp.hashtag_examples?.length > 0 && <div className="text-blue-600 text-xs">{fp.hashtag_examples.map((h) => `#${h}`).join(" ")}</div>}
            <div><span className="text-gray-500">Visual aesthetic:</span> {fp.visual_aesthetic}</div>
            <div><span className="text-gray-500">CTA style:</span> {fp.cta_style}</div>
            {fp.forbidden_topics?.length > 0 && <div><span className="text-gray-500">Avoid:</span> {fp.forbidden_topics.join(", ")}</div>}
            <p className="text-[10px] text-gray-400 mt-2">Generated from {fp._samples_count} samples on {fp._generated_at}</p>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Upload at least 5 sample posts then click "Generate from samples". This becomes the brain that produces on-brand content.</p>
        )}
      </div>

      {showHeyGenBrowser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowHeyGenBrowser(false)}>
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">HeyGen Browser</h2>
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                <button onClick={() => setHgTab("avatars")} className={`px-3 py-1 text-sm rounded ${hgTab === "avatars" ? "bg-white shadow text-[#7C3AED] font-semibold" : "text-gray-600"}`}>Avatars ({hgAvatars.length})</button>
                <button onClick={() => setHgTab("voices")} className={`px-3 py-1 text-sm rounded ${hgTab === "voices" ? "bg-white shadow text-[#7C3AED] font-semibold" : "text-gray-600"}`}>Voices ({hgVoices.length})</button>
              </div>
              <button onClick={() => setShowHeyGenBrowser(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {hgLoading ? (
                <p className="text-center text-gray-500 py-8">Loading HeyGen library…</p>
              ) : hgTab === "avatars" ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {hgAvatars.map((a) => (
                    <div key={a.avatar_id}
                         onClick={() => { setSettings({ ...settings, default_avatar_id: a.avatar_id }); toast.success(`Avatar selected: ${a.avatar_name}`); }}
                         className={`border rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition ${settings.default_avatar_id === a.avatar_id ? "ring-2 ring-[#7C3AED]" : "border-gray-200"}`}
                         data-testid={`heygen-avatar-${a.avatar_id}`}>
                      {a.preview_image_url ? <img src={a.preview_image_url} alt={a.avatar_name} className="w-full aspect-square object-cover" /> : <div className="aspect-square bg-gray-100" />}
                      <div className="p-2">
                        <p className="text-xs font-medium text-gray-900 truncate">{a.avatar_name}</p>
                        <p className="text-[10px] text-gray-500">{a.gender}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  {hgVoices.map((v) => (
                    <div key={v.voice_id}
                         className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${settings.default_voice_id === v.voice_id ? "bg-purple-50 ring-1 ring-[#7C3AED]" : ""}`}
                         onClick={() => { setSettings({ ...settings, default_voice_id: v.voice_id }); toast.success(`Voice selected: ${v.name}`); }}
                         data-testid={`heygen-voice-${v.voice_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{v.name}</p>
                        <p className="text-xs text-gray-500">{v.language} · {v.gender}</p>
                      </div>
                      {v.preview_audio && <audio src={v.preview_audio} controls className="h-8" onClick={(e) => e.stopPropagation()} />}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Selected — Avatar: <code>{settings.default_avatar_id || "none"}</code> · Voice: <code>{settings.default_voice_id || "none"}</code>
              </div>
              <button onClick={() => { setShowHeyGenBrowser(false); saveSettings(); }} className="op-btn-primary text-sm" data-testid="heygen-save-selection">Save & Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Manual IG Business ID Modal */}
      {showManualIg && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="manual-ig-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Instagram size={18} /> Enter IG Business Account ID
              </h3>
              <button onClick={() => setShowManualIg(false)} className="text-gray-400 hover:text-gray-700" data-testid="close-manual-ig">✕</button>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-900 space-y-1">
              <p className="font-semibold">How to find your IG Business Account ID:</p>
              <p>1. Open <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="underline">Meta Business Suite</a></p>
              <p>2. Go to <b>Settings → Accounts → Instagram</b></p>
              <p>3. Click your IG account → the numeric ID at the top is your <b>IG Business Account ID</b></p>
              <p className="mt-2 text-amber-800">If you don't see an IG account here, you must first link your IG to your Facebook Page in Meta Business Suite and switch it to <b>Business/Creator</b> mode.</p>
            </div>
            <label className="block text-xs text-gray-500 mb-1">IG Business Account ID (numeric)</label>
            <input
              className="op-input w-full mb-4"
              placeholder="e.g. 17841405822304914"
              value={manualIgId}
              onChange={(e) => setManualIgId(e.target.value)}
              data-testid="manual-ig-input"
            />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setShowManualIg(false)} className="op-btn-secondary text-sm" data-testid="manual-ig-cancel">Cancel</button>
              <button onClick={saveManualIg} disabled={savingManualIg} className="op-btn-primary text-sm" data-testid="manual-ig-save">
                {savingManualIg ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default BrandStyle;
